import { createCollapsibleFieldset } from './collapsible-fieldset.js';

/**
 * Shared <dialog> helpers so every modal behaves identically.
 *
 * Native <dialog> already gives us Escape-to-close and focus trapping. This
 * adds the one behaviour the platform doesn't: click-outside (on the backdrop)
 * to dismiss. We detect a backdrop click by checking that the target is the
 * dialog element itself — clicks on the dialog's children (the form, the
 * inputs) bubble up with those elements as the target, so they won't match.
 *
 * We require BOTH the mousedown AND the mouseup to land on the backdrop before
 * closing. A plain `click` listener also fires when a drag starts inside a
 * field and ends on the backdrop (e.g. selecting text in an input and dragging
 * past the modal edge), which would close the dialog and lose the user's work.
 * Gating on a matching mousedown+mouseup ignores those stray drag-releases.
 *
 * Note: this relies on the dialog's own padding being zero and an inner
 * wrapper (e.g. the <form>) filling it, which is how our `.modal` is styled —
 * otherwise clicks on the dialog's padding would count as "outside".
 *
 * @param {HTMLDialogElement} dialog
 */

/** Escape a plain string for safe use as innerHTML (legend text for collapsibles). */
function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export function enableBackdropClose(dialog, { onBackdropClose } = {}) {
  let downOnBackdrop = false;

  // Track where the press started. Only a press that begins on the backdrop is
  // a candidate for closing.
  dialog.addEventListener('mousedown', (event) => {
    downOnBackdrop = event.target === dialog;
  });

  // Close only when the release is also on the backdrop AND the press began
  // there. This ignores drags that start inside the modal and end outside.
  dialog.addEventListener('mouseup', (event) => {
    if (downOnBackdrop && event.target === dialog) {
      onBackdropClose?.();
      dialog.close();
    }
    downOnBackdrop = false;
  });
}

/**
 * Build a standard modal form field: a `<label>` (column layout, muted caption
 * via `.modal label`) wrapping a single `<input>`. Matches the markup the static
 * dialogs used, so it styles identically.
 *
 * @param {object} opts
 * @param {string} opts.label       Caption text shown above the input.
 * @param {string} [opts.name]      Input `name` (and `id`, if given).
 * @param {string} [opts.id]        Input `id` (defaults to `name`).
 * @param {string} [opts.type]      Input type (default 'text'); 'textarea' builds a
 *   multi-line <textarea> instead (with `opts.rows`).
 * @param {string} [opts.placeholder]
 * @param {boolean} [opts.required]
 * @param {string} [opts.value]     Initial value.
 * @param {string} [opts.autocomplete] Default 'off'.
 * @param {string} [opts.inputmode]
 * @returns {{ field: HTMLLabelElement, input: HTMLInputElement }}
 */
export function createField({
  label,
  labelNode,
  name,
  id = name,
  type = 'text',
  placeholder,
  required = false,
  value,
  autocomplete = 'off',
  inputmode,
  accept,
  min,
  max,
  step,
  rows,
} = {}) {
  const field = document.createElement('label');
  // A rich caption (e.g. text with an inline <span> unit) can be supplied via
  // `labelNode`; otherwise a plain text caption is used.
  field.append(labelNode ?? document.createTextNode(label ?? ''));

  // `type: 'textarea'` builds a multi-line <textarea> (optionally `rows` tall).
  const isTextarea = type === 'textarea';
  const input = document.createElement(isTextarea ? 'textarea' : 'input');
  if (isTextarea) {
    if (rows !== undefined) input.rows = rows;
  } else {
    input.type = type;
  }
  if (name) input.name = name;
  if (id) input.id = id;
  if (placeholder !== undefined) input.placeholder = placeholder;
  if (required) input.required = true;
  if (value !== undefined) input.value = value;
  // File inputs reject a set `autocomplete`; only apply it to text-like inputs.
  if (autocomplete && type !== 'file') input.autocomplete = autocomplete;
  if (inputmode) input.inputMode = inputmode;
  if (accept !== undefined) input.accept = accept;
  if (min !== undefined) input.min = String(min);
  if (max !== undefined) input.max = String(max);
  if (step !== undefined) input.step = String(step);

  field.appendChild(input);
  return { field, input };
}

/**
 * Build a standard inline checkbox row: a `<label class="checkbox-row">` with the
 * box beside its text (the shared modal checkbox convention).
 *
 * @param {object} opts
 * @param {string} opts.label
 * @param {string} [opts.name]
 * @param {string} [opts.id]
 * @param {boolean} [opts.checked]
 * @returns {{ field: HTMLLabelElement, input: HTMLInputElement }}
 */
export function createCheckboxRow({ label, name, id = name, checked = false } = {}) {
  const field = document.createElement('label');
  field.className = 'checkbox-row';
  const input = document.createElement('input');
  input.type = 'checkbox';
  if (name) input.name = name;
  if (id) input.id = id;
  input.checked = checked;
  field.append(input, document.createTextNode(label ?? ''));
  return { field, input };
}

/**
 * Build a standard modal select field: a `<label>` (matching `createField`)
 * wrapping a `<select>`. Options can be passed up front or populated later via
 * the returned `select`.
 *
 * @param {object} opts
 * @param {string} opts.label
 * @param {string} [opts.name]
 * @param {string} [opts.id]
 * @param {boolean} [opts.required]
 * @param {Array<{value: string, label: string, selected?: boolean}>} [opts.options]
 * @returns {{ field: HTMLLabelElement, select: HTMLSelectElement }}
 */
export function createSelectField({ label, name, id = name, required = false, options = [] } = {}) {
  const field = document.createElement('label');
  field.append(document.createTextNode(label ?? ''));
  const select = document.createElement('select');
  if (name) select.name = name;
  if (id) select.id = id;
  if (required) select.required = true;
  let selectedValue = null;
  for (const opt of options) {
    const o = document.createElement('option');
    o.value = opt.value;
    o.textContent = opt.label;
    if (opt.selected) selectedValue = opt.value;
    select.appendChild(o);
  }
  // Select once every option is attached (flagging a detached option's
  // `selected` isn't honoured consistently across DOM implementations).
  if (selectedValue !== null) select.value = selectedValue;
  field.appendChild(select);
  return { field, select };
}

/**
 * Lay two or more fields out horizontally in a row (they stack on very narrow
 * screens). Pass the field elements (e.g. `createField(...).field`).
 *
 * @param {Node[]} children  The field elements to place in the row.
 * @param {object} [opts]
 * @param {'equal'|'auto'} [opts.layout='equal']
 *   'equal' → a grid of equal-width columns (each shrinks to fit; stacks narrow).
 *   'auto'  → flex where the LAST child hugs its content and the rest fill the
 *             remaining width (e.g. a number field beside a checkbox).
 * @returns {HTMLDivElement}
 */
export function createFieldRow(children = [], { layout = 'equal' } = {}) {
  const row = document.createElement('div');
  row.className = `field-row field-row--${layout}`;
  for (const child of children) if (child) row.appendChild(child);
  return row;
}

/**
 * Build a `<fieldset>` group with a `<legend>` and a set of child controls. The
 * bordered framing + legend styling come from the shared `.modal fieldset` rule.
 * When `collapsible` is true this delegates to `createCollapsibleFieldset` so
 * there's a single fieldset entry point.
 *
 * @param {object} opts
 * @param {string} opts.legend
 * @param {Node[]} [opts.children]
 * @param {string} [opts.className]      Extra class(es) on the fieldset.
 * @param {boolean} [opts.collapsible]   Render a collapsible (expandable) group.
 * @param {boolean} [opts.expanded]      Initial expanded state (collapsible only).
 * @param {boolean} [opts.feature]       Use the brand "feature" collapsible style.
 * @param {Function} [opts.onToggle]     Collapsible toggle callback.
 * @returns {HTMLFieldSetElement | {fieldset, content, ...}}
 *   A plain `<fieldset>` when not collapsible; otherwise the createCollapsibleFieldset result.
 */
export function createFieldset({
  legend,
  children = [],
  className,
  collapsible = false,
  expanded = false,
  feature = false,
  onToggle,
} = {}) {
  if (collapsible) {
    // Delegate to the collapsible builder, mapping our generic {legend, children}
    // onto its {legendHtml, content} contract (children wrapped in a fragment).
    const content = document.createDocumentFragment();
    for (const child of children) if (child) content.appendChild(child);
    return createCollapsibleFieldset({
      legendHtml: escapeHtml(legend ?? ''),
      content,
      expanded,
      feature,
      onToggle,
    });
  }
  const fieldset = document.createElement('fieldset');
  if (className) fieldset.className = className;
  const legendEl = document.createElement('legend');
  legendEl.textContent = legend ?? '';
  fieldset.appendChild(legendEl);
  for (const child of children) if (child) fieldset.appendChild(child);
  return fieldset;
}

/**
 * Build a standard modal shell — one consistent structure + behaviour for every
 * dialog: `<dialog class="modal"> > <form method="dialog"> > .modal-header
 * (h3 + close ✕) + body + .modal-actions (Cancel + submit)`. The dialog is
 * appended to `<body>` once; callers populate `body` (e.g. with `createField`)
 * and drive it via `open()` / `close()`.
 *
 * Wires the shared dismissal behaviour (Escape via native <dialog>, backdrop
 * click via `enableBackdropClose`, the ✕ and Cancel buttons) and routes form
 * submit to `onSubmit`. `onSubmit` may be async; return `false` (or throw) to
 * keep the dialog open (e.g. on validation failure or an error).
 *
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} [opts.className]     Extra class(es) on the <dialog>.
 * @param {string} [opts.submitLabel]   Primary button text; omit for no submit button.
 * @param {?string} [opts.cancelLabel]  Secondary button text (default 'Cancel'); null to omit.
 * @param {Array<{label: string, variant?: string, icon?: string, onClick?: Function, closeOnClick?: boolean}>} [opts.actions]
 *   Custom footer buttons. When given, they REPLACE the default Cancel/submit
 *   footer (e.g. a read-only dialog with "Edit" + "Close"). `variant` sets the
 *   button class ('secondary' by default); `closeOnClick` (default true) closes
 *   the dialog after `onClick`.
 * @param {(form: HTMLFormElement, api: object) => any} [opts.onSubmit]
 * @param {(info: { keptDraft: boolean }) => void} [opts.onClose]
 *   Called after the dialog closes. `keptDraft` is true when the close was a
 *   backdrop dismissal on a `keepDraftOnDismiss` modal — skip any form reset then,
 *   so the draft survives for `resumeDraft`.
 * @param {boolean} [opts.keepDraftOnDismiss]
 *   Editor modals: a deliberate backdrop click closes but KEEPS the typed input.
 *   Callers open with `open({ draftKey })` (e.g. the item id, or 'new') and, at the
 *   top of their open handler, `if (modal.resumeDraft(key)) return;` — which
 *   reopens untouched when the last close was a backdrop dismissal of the SAME key.
 *   Cancel / ✕ / Escape / a successful submit (any other close) discard the draft.
 * @returns {{ dialog, form, body, open, close, resumeDraft, setTitle, setBusy, setSubmitEnabled }}
 */
export function createModal({
  title,
  className,
  submitLabel,
  cancelLabel = 'Cancel',
  actions: customActions,
  onSubmit,
  onClose,
  keepDraftOnDismiss = false,
} = {}) {
  const dialog = document.createElement('dialog');
  dialog.className = className ? `modal ${className}` : 'modal';

  const form = document.createElement('form');
  form.method = 'dialog';

  const header = document.createElement('div');
  header.className = 'modal-header';
  const heading = document.createElement('h3');
  heading.textContent = title ?? '';
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'icon-button';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';
  header.append(heading, closeBtn);

  const body = document.createElement('div');
  // The scrollable middle region: a real flex column (`.modal-body`) so that on a
  // tall modal only the body scrolls while the header (title + ✕) and the actions
  // footer stay pinned. Owns its own field-flow gap + padding via `.modal-body`.
  body.className = 'modal-body';

  const actions = document.createElement('div');
  actions.className = 'modal-actions';

  let cancelBtn = null;
  let submitBtn = null;
  // Custom footer buttons replace the default Cancel/submit pair entirely. Their
  // click handlers are wired after `api` exists (below) so they can call it.
  const customButtons = [];
  if (Array.isArray(customActions) && customActions.length > 0) {
    for (const action of customActions) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = action.variant ?? 'secondary';
      // Optional leading Font Awesome icon (e.g. 'fa-bolt'); label follows.
      if (action.icon) {
        btn.innerHTML = `<i class="fa-solid ${escapeHtml(action.icon)}" aria-hidden="true"></i> `;
        btn.append(action.label);
      } else {
        btn.textContent = action.label;
      }
      customButtons.push({ btn, action });
      actions.appendChild(btn);
    }
  } else {
    if (cancelLabel !== null) {
      cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.className = 'secondary';
      cancelBtn.textContent = cancelLabel;
      actions.appendChild(cancelBtn);
    }
    if (submitLabel) {
      submitBtn = document.createElement('button');
      submitBtn.type = 'submit';
      submitBtn.textContent = submitLabel;
      actions.appendChild(submitBtn);
    }
  }

  form.append(header, body);
  // Only add the actions row if it actually holds a button — a picker-style modal
  // with no submit and no cancel (dismiss via ✕/Escape/backdrop) shouldn't render
  // an empty footer row.
  if (actions.childElementCount > 0) form.appendChild(actions);
  dialog.appendChild(form);
  document.body.appendChild(dialog);

  // Draft retention: which item the open dialog is editing, whether the pending
  // close came from the backdrop, and (after such a close) the key whose draft the
  // DOM still holds. Any other close path clears the held draft.
  let openKey = null;
  let closingViaBackdrop = false;
  let heldDraftKey = null;
  enableBackdropClose(dialog, {
    onBackdropClose: () => {
      closingViaBackdrop = true;
    },
  });
  closeBtn.addEventListener('click', () => dialog.close());
  if (cancelBtn) cancelBtn.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => {
    const keptDraft = keepDraftOnDismiss && closingViaBackdrop;
    heldDraftKey = keptDraft ? openKey : null;
    closingViaBackdrop = false;
    onClose?.({ keptDraft });
  });

  // Keep the dialog centred on the VISUAL VIEWPORT while open. A native
  // showModal() dialog is position:fixed but its containing block is the initial
  // containing block, whose WIDTH follows the document — so on pages that make the
  // document wider than the screen (the itinerary read-only view forces a fixed
  // 21cm-wide <main> for its A4 preview), `inset:0; margin:auto` centres against
  // the 21cm document and the dialog lands off-screen; reaching it means scrolling
  // (which touches the backdrop and closes it). We can't drop the wide document
  // (it powers the horizontal-scrolling A4 preview + its sticky toolbar), so
  // instead pin the dialog to the visual viewport in JS — the same
  // `window.visualViewport` the toolbar hack uses — so it's centred on what the
  // user actually sees, on every page, on desktop and mobile.
  const vv = window.visualViewport ?? null;
  const centreOnViewport = () => {
    if (!dialog.open) return;
    const w = vv ? vv.width : window.innerWidth;
    const h = vv ? vv.height : window.innerHeight;
    // Fit the dialog to the VISIBLE height. A mobile on-screen keyboard shrinks
    // the visual viewport but not `dvh`, so a 92dvh dialog would run under the
    // keyboard; capping it to the visual viewport keeps the whole dialog above
    // the keyboard with only its body scrolling (CSS falls back to 92dvh).
    if (vv) dialog.style.setProperty('--modal-max-height', `${Math.floor(h * 0.92)}px`);
    // Centre by computing the top-left corner from the dialog's MEASURED size,
    // NOT via `transform: translate(-50%, -50%)`. A `transform` on the dialog
    // would make it the containing block for `position: fixed` DESCENDANTS
    // (e.g. the autocomplete dropdown), offsetting them by the dialog's position
    // — the dropdown would land down-and-right of its input. Clamp to >=0 so a
    // dialog taller/wider than the viewport stays reachable from the top-left.
    const left = Math.max(0, (vv ? vv.offsetLeft : 0) + (w - dialog.offsetWidth) / 2);
    const top = Math.max(0, (vv ? vv.offsetTop : 0) + (h - dialog.offsetHeight) / 2);
    dialog.style.left = `${left}px`;
    dialog.style.top = `${top}px`;
    dialog.style.right = 'auto';
    dialog.style.bottom = 'auto';
    dialog.style.margin = '0';
    dialog.style.transform = 'none';
  };
  // When the visible area shrinks (keyboard opening), re-fit + re-centre, then
  // bring the focused field back into view inside the now-shorter body.
  const onViewportResize = () => {
    centreOnViewport();
    const focused = document.activeElement;
    if (dialog.open && focused && focused !== dialog && dialog.contains(focused)) {
      focused.scrollIntoView?.({ block: 'nearest' });
    }
  };
  if (vv) {
    vv.addEventListener('resize', onViewportResize);
    vv.addEventListener('scroll', centreOnViewport);
  }
  // The dialog's height changes while open (sections shown/hidden, rows
  // added) — re-centre then too, or a grown dialog runs off the bottom.
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(centreOnViewport).observe(dialog);
  }

  const api = {
    dialog,
    form,
    body,
    /** Open fresh. `draftKey` identifies what's being edited (for resumeDraft). */
    open({ draftKey = 'default' } = {}) {
      openKey = String(draftKey);
      heldDraftKey = null;
      dialog.showModal();
      centreOnViewport();
    },
    /**
     * Reopen with the form exactly as left, IF the last close was a backdrop
     * dismissal while editing `draftKey`. Returns true when resumed (the caller
     * must then skip re-populating); false otherwise (caller populates + `open`).
     */
    resumeDraft(draftKey = 'default') {
      if (!keepDraftOnDismiss || heldDraftKey === null || heldDraftKey !== String(draftKey)) {
        return false;
      }
      openKey = heldDraftKey;
      heldDraftKey = null;
      dialog.showModal();
      centreOnViewport();
      return true;
    },
    close() {
      dialog.close();
    },
    /** Update the header title (e.g. a picker reused for "Add" vs "Replace"). */
    setTitle(text) {
      heading.textContent = text ?? '';
    },
    /**
     * Disable the action buttons during an async submit (e.g. a network fetch).
     * When `busyLabel` is given, the submit button also shows it while busy and
     * restores its original label when cleared.
     */
    setBusy(busy, busyLabel) {
      if (submitBtn) {
        submitBtn.disabled = busy;
        if (busyLabel !== undefined) {
          if (busy) {
            submitBtn.dataset.idleLabel = submitBtn.textContent;
            submitBtn.textContent = busyLabel;
          } else if (submitBtn.dataset.idleLabel !== undefined) {
            submitBtn.textContent = submitBtn.dataset.idleLabel;
            delete submitBtn.dataset.idleLabel;
          }
        }
      }
      if (cancelBtn) cancelBtn.disabled = busy;
    },
    /** Whether the submit button is currently disabled (busy). */
    get busy() {
      return !!(submitBtn && submitBtn.disabled);
    },
    /**
     * Enable/disable ONLY the submit button, leaving Cancel/✕ active. Use this
     * for validation gating (e.g. "disabled until a file is chosen") — unlike
     * `setBusy`, which also disables Cancel for an in-flight async operation.
     */
    setSubmitEnabled(enabled) {
      if (submitBtn) submitBtn.disabled = !enabled;
    },
  };

  // Wire custom footer buttons now that `api` exists.
  for (const { btn, action } of customButtons) {
    btn.addEventListener('click', () => {
      action.onClick?.(api);
      if (action.closeOnClick !== false) dialog.close();
    });
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!onSubmit) return;
    const result = await onSubmit(form, api);
    // Returning false keeps the dialog open (validation / error paths close it
    // themselves or leave it open); anything else is a successful submit and the
    // caller is responsible for closing (so it can navigate/toast first).
    if (result === false) return;
  });

  return api;
}
